import { useEffect, useState } from "react";
import { Loader2, Gauge, Info } from "lucide-react";
import { api } from "../../lib/api";

const CORE = ["LCP", "INP", "CLS"];
const ALL = ["LCP", "INP", "CLS", "FCP", "TTFB"];
const LABEL = {
  LCP: "Largest Contentful Paint", INP: "Interaction to Next Paint",
  CLS: "Cumulative Layout Shift", FCP: "First Contentful Paint",
  TTFB: "Time to First Byte",
};
const ratingCls = {
  good: "bg-green-100 text-green-800",
  "needs-improvement": "bg-amber-100 text-amber-800",
  poor: "bg-red-100 text-red-700",
};

function fmt(name, v) {
  if (v == null) return "—";
  if (name === "CLS") return v.toFixed(3);
  if (name === "LCP" || name === "FCP") return `${(v / 1000).toFixed(2)} s`;
  return `${Math.round(v)} ms`;
}

function MetricCell({ name, m }) {
  if (!m) return <span className="text-mid-gray">—</span>;
  return (
    <div className="flex items-center gap-2">
      <span className="font-semibold text-anthracite">{fmt(name, m.p75)}</span>
      <span className={`rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase ${ratingCls[m.rating] || "bg-light-gray text-mid-gray"}`}>
        {m.rating === "needs-improvement" ? "mittel" : m.rating === "good" ? "gut" : "schwach"}
      </span>
    </div>
  );
}

export default function Performance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/vitals", { params: { days } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <div data-testid="admin-performance">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-anthracite">Performance · Core Web Vitals</h1>
          <p className="mt-1 text-mid-gray">Reale Nutzerwerte (p75), anonym erfasst – ohne personenbezogene Daten.</p>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-sm border border-light-gray bg-white px-3 py-2 text-sm outline-none focus:border-yellow" data-testid="vitals-days">
          <option value={7}>Letzte 7 Tage</option>
          <option value={30}>Letzte 30 Tage</option>
          <option value={90}>Letzte 90 Tage</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-yellow" /></div>
      ) : !data || data.total_samples === 0 ? (
        <div className="mt-10 flex flex-col items-center rounded-md border border-dashed border-mid-gray/30 bg-white p-12 text-center" data-testid="vitals-empty">
          <Gauge className="h-8 w-8 text-mid-gray" />
          <h2 className="mt-4 font-heading text-lg font-bold text-anthracite">Noch keine Messwerte</h2>
          <p className="mt-2 max-w-md text-sm text-mid-gray">
            Sobald Besucher die öffentliche Website aufrufen, werden hier LCP, INP und CLS pro Seite ausgewertet.
            Öffnen Sie die Website in einem neuen Tab, um erste Werte zu erzeugen.
          </p>
        </div>
      ) : (
        <>
          {/* Gesamt-Übersicht (Kern-Vitals) */}
          <div className="mt-6 grid gap-4 sm:grid-cols-3" data-testid="vitals-overall">
            {CORE.map((name) => {
              const m = data.overall[name];
              return (
                <div key={name} className="rounded-md border border-light-gray bg-white p-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-mid-gray">{name}</p>
                  <p className="mt-1 text-3xl font-bold text-anthracite">{m ? fmt(name, m.p75) : "—"}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-mid-gray">{LABEL[name]}</span>
                    {m && <span className={`rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase ${ratingCls[m.rating]}`}>{m.rating === "needs-improvement" ? "mittel" : m.rating === "good" ? "gut" : "schwach"}</span>}
                  </div>
                  {m && <p className="mt-1 text-[11px] text-mid-gray">{m.count} Messungen</p>}
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex items-start gap-2 rounded-sm bg-light-gray p-4 text-sm text-mid-gray">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Bewertung nach Google-Schwellen (p75): LCP gut ≤ 2,5 s · INP gut ≤ 200 ms · CLS gut ≤ 0,1. Insgesamt {data.total_samples} Messungen in {data.days} Tagen.</span>
          </div>

          {/* Pro Seite (Ortsseiten zuerst) */}
          <div className="mt-6 overflow-x-auto rounded-md border border-light-gray bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-light-gray bg-light-gray text-left text-xs uppercase tracking-wider text-mid-gray">
                  <th className="px-4 py-3">Seite</th>
                  {ALL.map((n) => <th key={n} className="px-4 py-3">{n}</th>)}
                  <th className="px-4 py-3">Messungen</th>
                </tr>
              </thead>
              <tbody>
                {data.pages.map((p) => (
                  <tr key={p.path} className={`border-b border-light-gray/60 hover:bg-light-gray/40 ${p.path.startsWith("/entruempelung/") ? "bg-yellow/5" : ""}`} data-testid={`vitals-row-${p.path}`}>
                    <td className="px-4 py-3 font-medium text-anthracite">{p.path}</td>
                    {ALL.map((n) => <td key={n} className="px-4 py-3"><MetricCell name={n} m={p.metrics[n]} /></td>)}
                    <td className="px-4 py-3 text-mid-gray">{p.samples}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
